import { useContext, useMemo } from 'react';
import { useAgents } from '@/api/hooks';
import type { WireAgent } from '@/api/wire';
import { useDepartmentNames } from '@/app/departments';
import type { Me } from '@/api/session';
import { MeContext, useMeOrThrow } from '@/app/MeContext';
import type { Party } from './context';
import { twinLabel } from './people';

/**
 * Who is who, for the panel that says who brought a record, who owns it and
 * who knows more (ADR-0155). Everything is read from what the platform
 * already answers every signed-in person — the department listing (its lead
 * and its agents) and the agents the person may see — so a builder never
 * guesses a name and never reaches for one it may not show.
 *
 * What it will not do: link a person or an agent to a page the reader may
 * not open. An agent's page is the superadmin's (`TECHNICAL_DOORS`); a
 * colleague has no page of their own that everyone can read. So a party is
 * a name and what it is, and a link only where the reader may follow it.
 */

/** "iryna@norumfoods.com" → "Iryna"; "intars.olbiks@…" → "Intars Olbiks". */
export function nameFromAddress(address: string): string {
  const local = address.split('@')[0] ?? address;
  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length === 0) return address;
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export interface Who {
  /** The reader. */
  me: Party;
  /**
   * An actor the platform records by address or handle — a person's email,
   * `otis.agent@…`, `sol.twin` — as a party. The reader is "You". Null for
   * nothing at all; an unknown address reads as the name its first part makes.
   */
  actor: (id: string | null | undefined) => Party | null;
  /** The department as a party that "knows more". */
  department: (name: string | null | undefined) => Party | null;
  /** Its lead, when it has one. */
  lead: (name: string | null | undefined) => Party | null;
  /** Its agents the reader may see, live ones only. */
  agentsOf: (name: string | null | undefined) => Party[];
  /** Who to ask in a department: its lead, then its agents. Never the reader. */
  knowers: (name: string | null | undefined) => Party[];
  /** The department's name in words. */
  label: (name: string) => string;
  /** Whether the reader is the superadmin, who alone may follow a link to an agent's page. */
  superadmin: boolean;
  /** The department the reader's session is in. */
  home: string;
  /** The reader's own name as the platform holds it, to say "You" where a reply names them by name. */
  selfName: string;
}

const agentParty = (a: WireAgent, superadmin: boolean): Party => ({
  name: a.agentKind === 'twin' ? (a.principal ? twinLabel(a.givenName, a.principal.displayName) : `${a.givenName} · a twin`) : `${a.givenName} Agent`,
  kind: a.agentKind === 'twin' ? 'twin' : 'agent',
  ...(superadmin ? { to: `/agents/${a.id}` } : {}),
});

export function useWho(): Who {
  return useWhoOf(useMeOrThrow())!;
}

/**
 * `useWho` for a component that also renders outside the signed-in frame —
 * the refusal page, which the shell shows when `/auth/me` itself is refused.
 * Outside it there is nobody to say "You" to and nothing it may ask the
 * platform, so it asks nothing and answers null; the caller says what the
 * rule says instead of naming anyone.
 */
export function useWhoMaybe(): Who | null {
  return useWhoOf(useContext(MeContext));
}

function useWhoOf(me: Me | null): Who | null {
  const departments = useDepartmentNames(me !== null);
  const agents = useAgents(false, me !== null).data?.agents;
  return useMemo<Who | null>(() => {
    if (!me) return null;
    const byHandle = new Map<string, WireAgent>();
    for (const a of agents ?? []) {
      byHandle.set(a.handle, a);
      if (a.workspaceEmail) byHandle.set(a.workspaceEmail, a);
    }
    const you: Party = { name: 'You', kind: 'person' };
    const lead = (name: string | null | undefined): Party | null => {
      const l = name ? departments.info(name)?.lead : null;
      if (!l) return null;
      return l.id === me.humanId ? { name: 'You', kind: 'person', role: `lead of ${departments.label(name!)}` } : { name: l.displayName, kind: 'person', role: `leads ${departments.label(name!)}` };
    };
    const agentsOf = (name: string | null | undefined): Party[] =>
      name
        ? (agents ?? [])
            .filter((a) => a.department === name && a.agentKind === 'department' && !a.revokedAt)
            .map((a) => agentParty(a, me.isSuperadmin))
        : [];
    return {
      me: you,
      actor: (id) => {
        if (!id) return null;
        if (id.toLowerCase() === me.email.toLowerCase()) return you;
        const known = byHandle.get(id);
        if (known) return agentParty(known, me.isSuperadmin);
        if (id.endsWith('.twin')) return { name: `${id.slice(0, 1).toUpperCase()}${id.slice(1, -'.twin'.length)} · a twin`, kind: 'twin' };
        if (id.includes('.agent@')) return { name: `${nameFromAddress(id).replace(/ Agent$/, '')} Agent`, kind: 'agent' };
        if (id === 'system' || id === 'task-runner' || id === 'heartbeat') return { name: 'The platform', kind: 'system' };
        return { name: nameFromAddress(id), kind: 'person' };
      },
      department: (name) => (name ? { name: departments.label(name), kind: 'department' } : null),
      lead,
      agentsOf,
      knowers: (name) => {
        const l = lead(name);
        return [...(l && l.name !== 'You' ? [l] : []), ...agentsOf(name)];
      },
      label: departments.label,
      superadmin: me.isSuperadmin,
      home: me.department,
      selfName: me.displayName,
    };
  }, [me, departments, agents]);
}

/**
 * A `Who` for a unit test of a builder: names by their address, one lead
 * and one agent for any department. Not for production; it is here so each
 * feature's builder is tested without a network.
 */
export function fakeWho(over: Partial<Who> = {}): Who {
  const you: Party = { name: 'You', kind: 'person' };
  return {
    me: you,
    actor: (id) => (id ? (id === 'me@norumfoods.com' ? you : id.includes('.agent@') ? { name: `${nameFromAddress(id).replace(/ Agent$/, '')} Agent`, kind: 'agent' } : { name: nameFromAddress(id), kind: 'person' }) : null),
    department: (name) => (name ? { name: name.charAt(0).toUpperCase() + name.slice(1), kind: 'department' } : null),
    lead: (name) => (name ? { name: 'Lena', kind: 'person', role: `leads ${name}` } : null),
    agentsOf: (name) => (name ? [{ name: 'Otis Agent', kind: 'agent' }] : []),
    knowers: (name) => (name ? [{ name: 'Lena', kind: 'person', role: `leads ${name}` }, { name: 'Otis Agent', kind: 'agent' }] : []),
    label: (name) => name.charAt(0).toUpperCase() + name.slice(1),
    superadmin: false,
    home: 'outbound',
    selfName: 'Me',
    ...over,
  };
}
